<template>
  <div v-if="!$apollo.loading">
    <div class="not-available">
      <h1>Sorry, Design Tool is currently only available for Desktop.</h1>
    </div>
    <v-layout class="design-tool-container" row>
      <v-flex class="left-container" xs4>
        <elements :fields="fields" :zones="availableZones" :extra-modules="extraModules" />
      </v-flex>
      <v-flex class="upload-container right-container" xs8>
        <div v-if="activeItemIndex !== -1" class="builder-toolbar elevation-2">
          <text-toolbar v-if="activeToolbar === 'text'" :styles.sync="styles" />
          <color-toolbar
            v-if="activeToolbar === 'color'"
            :styles.sync="styles"
            :extra-modules="extraModules"
          />
        </div>
        <div v-else class="builder-toolbar elevation-2">
          <span>Add or select a component to see the options</span>
        </div>
        <div v-if="showPreviewDialog" class="v-overlay v-overlay--active" />
        <preview
          v-if="showPreviewDialog"
          :background-image-url="backgroundImageURL"
          :items="droppedItems"
          :height="toPixels(getHeight())"
          :width="toPixels(getWidth())"
          :accreditation="previewData"
          :attendee-fields="fields"
          class="drop drop-zone elevation-24"
          style="z-index: 5"
        />
        <drop
          v-else
          :style="[
            {
              backgroundSize: 'cover',
              height: toPixels(getHeight()) + 'px',
              width: toPixels(getWidth()) + 'px',
            },
            backgroundImageURL && !backgroundImageURL.startsWith('data:image')
              ? { backgroundImage: 'url(' + backgroundImageURL + ')' }
              : {},
          ]"
          class="drop drop-zone elevation-2"
          @drop="handleDrop"
        >
          <template v-if="renderBuilder">
            <div :style="{ height: '100%' }">
              <div
                v-if="backgroundImageURL && backgroundImageURL.startsWith('data:image')"
                :style="{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }"
              >
                <spinner />
              </div>
              <vue-draggable-resizable
                v-for="(item, index) in droppedItems"
                v-else
                :ref="`${item.id}`"
                :key="item.id"
                :resizable="item.resizable"
                :w="item.w"
                :h="item.h"
                :z="item.active ? 100 : item.z"
                :x="item.x"
                :y="item.y"
                :minw="10"
                :minh="5"
                :grid="[GRID_SIZE, GRID_SIZE]"
                :active.sync="item.active"
                :handles="['tm', 'ml', 'bm', 'mr']"
                :class="['parent-container', { 'active-item': item.active }]"
                :style="`transform: rotate(${item.styles.rotate}deg)`"
                :parent="true"
                axis="both"
                @resizestop="(x, y, w, h) => onResize(index, item.type, { x, y, w, h })"
                @dragstop="(x, y) => updateItem(index, { x, y })"
                @activated="() => onFocusItem(item, index)"
              >
                <div v-if="item.active" class="item-action delete" @click="deleteItem(item, index)">
                  <v-icon small color="white">close</v-icon>
                </div>
                <div v-if="item.active" class="item-action rotate" @click="rotateItem(item, index)">
                  <v-icon small color="white">rotate_right</v-icon>
                </div>
                <div
                  v-if="item.active && item.type === 'zone'"
                  class="item-action upload-zone-image"
                  @click="uploadZoneImage(index, item)"
                >
                  <v-icon small color="white">camera</v-icon>
                </div>
                <item :event-id="eventId" :data="item" class="no-select" />
              </vue-draggable-resizable>
            </div>
          </template>
        </drop>
        <v-layout class="button-container" justify-center>
          <main-button v-if="!showPreviewDialog" depressed text="Save" @click="saveBuilder()" />
          <options-menu v-if="!showPreviewDialog" :options.sync="options" />
          <main-button
            :text="showPreviewDialog ? 'Exit preview' : 'Preview'"
            :flat="!showPreviewDialog"
            :depressed="showPreviewDialog"
            style="z-index: 5"
            @click="() => (showPreviewDialog = !showPreviewDialog)"
          />
          <main-button
            v-if="!backgroundImageURL"
            flat
            text="Upload background"
            @click="openFSPicker"
          />
          <main-button
            v-if="backgroundImageURL"
            flat
            color="error"
            text="Remove background"
            @click="backgroundImageURL = null"
          />
        </v-layout>
      </v-flex>
    </v-layout>
    <simple-snackbar :open.sync="snackbarOpen" color="primary" message-prop="Design saved" />
    <hidden-file-input ref="file-input" max-file-size-mb="10" @file="processBackground($event)" />
    <hidden-file-input ref="upload-zone-image" max-file-size-mb="1" @file="processImage($event)" />
    <cropper-dialog
      :aspect-ratio="getWidth() / getHeight()"
      :image="backgroundImageURL"
      @dismissed="backgroundImageURL = null"
      @cropped="uploadBackground($event)"
    />
  </div>
</template>

<script>
import { SAVE_BUILDER } from '../../graphql/builder.graphql'
import { GET_EVENT_BUILDER } from '../../graphql/event.graphql'

import {
  toCSS,
  fileToDataUrl,
  getMimeFromDataURL,
  dataURLToImage,
  resizeImage,
  fileToImage,
  objectToArray,
} from './../../utils'
import { getSignedURL, uploadImage } from './../../http'

import Item from './item.vue'
import Preview from './preview.vue'
import ColorToolbar from './color-toolbar.vue'
import TextToolbar from './text-toolbar.vue'
import Elements from './elements.vue'
import OptionsMenu from './options-menu.vue'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import { defaultStyles, formats, mmToPixel, previewData } from './builder.utils'

import HiddenFileInput from './../shared/hidden-file-input'
import CropperDialog from './../shared/dialogs/cropper-dialog'
import { getSampleFieldsValue } from './../accreditations/attendee-fields.js'
import Spinner from '../shared/spinner'

const uuidv4 = require('uuid/v4')

export default {
  name: 'accreditation-builder',
  components: {
    TextToolbar,
    ColorToolbar,
    OptionsMenu,
    Elements,
    Preview,
    Item,
    SimpleSnackbar,
    HiddenFileInput,
    CropperDialog,
    Spinner,
  },
  props: {
    eventId: String,
  },
  data() {
    return {
      defaultStyles,
      extraModules: {},
      renderBuilder: true,
      backgroundImageURL: null,
      droppedItems: [],
      showPreviewDialog: false,
      availableZones: [],
      activeToolbar: '',
      activeItemIndex: -1,
      permission: '',
      styles: {},
      fields: [],
      formats,
      options: {},
      previewData: previewData,
      GRID_SIZE: mmToPixel(0.01),
      snackbarOpen: false,
      activeZoneIndex: -1,
    }
  },
  watch: {
    options: {
      handler({ format }) {
        const height = this.toPixels(format.height)
        const width = this.toPixels(format.width)
        const scaleItem = item => this.translateItem(item, width, height)
        const translatedItems = this.droppedItems.map(scaleItem)
        this.droppedItems = translatedItems
        this.renderBuilder = false
        this.$nextTick(() => {
          this.renderBuilder = true
        })
      },
      deep: true,
    },
    styles: {
      handler(values) {
        const index = this.activeItemIndex
        const item = this.droppedItems[index]
        const ref = this.$refs[item.id]
        let size = {}
        if (ref) {
          const { width, height } = ref[0].$el.getBoundingClientRect()
          size = { w: width, h: height }
        }

        if (item.type === 'zone') {
          const indexes = this.droppedItems
            .map(({ type }, i) => ({ type, i }))
            .filter(({ type }) => type === 'zone')
          indexes.forEach(({ i }) => {
            const {
              styles: { backgroundImage },
            } = this.droppedItems[i]
            this.updateItem(i, {
              ...size,
              styles: { ...item.styles, ...values, backgroundImage },
              active: true,
            })
          })
        } else {
          this.updateItem(index, { ...size, styles: { ...item.styles, ...values }, active: true })
        }
      },
      deep: true,
    },
  },
  methods: {
    async processBackground(file) {
      this.backgroundImageURL = await fileToDataUrl(file)
    },
    getHeight() {
      const { orientation, format } = this.options
      const orientationChanged = orientation !== format.defaultOrientation

      return !orientationChanged ? format.height : format.width
    },
    getWidth() {
      const { orientation, format } = this.options
      const orientationChanged = orientation !== format.defaultOrientation

      return !orientationChanged ? format.width : format.height
    },
    translateItem(item, width, height) {
      const outOfbounds = item.y >= height || item.x >= width
      if (outOfbounds) {
        return {
          ...item,
          x: 0,
          y: 0,
        }
      }
      const w = item.x + item.w > width ? width - item.x : item.w
      const h = item.y + item.h > height ? height - item.y : item.h

      return {
        ...item,
        w,
        h,
      }
    },
    toPixels(mm) {
      return mmToPixel(mm)
    },
    rotateItem({ styles, w, h, ...rest }, index) {
      const alreadyRotated = styles.rotate
      const angle = alreadyRotated ? (styles.rotate + 90) % 360 : 90
      this.updateItem(index, { ...rest, styles: { ...styles, rotate: angle } })
      this.renderBuilder = false
      this.$nextTick(() => {
        this.renderBuilder = true
      })
    },
    onFocusItem(item, index) {
      const { styles, toolbar } = item
      this.styles = styles
      this.activeToolbar = toolbar
      this.activeItemIndex = index
    },
    onResize(index, type, { x, y, w, h }) {
      this.updateItem(index, {
        x,
        y,
        w,
        h,
      })
    },
    updateItem(index, properties) {
      const newItem = {
        ...this.droppedItems[index],
        ...properties,
      }
      this.droppedItems.splice(index, 1, newItem)
    },
    getStyles(data) {
      return toCSS(data)
    },
    deleteItem(item, index) {
      if (item.active) {
        this.activeItemIndex = -1
      }
      if (item.type === 'zone') {
        this.availableZones.push(item.placeholder)
        this.availableZones = this.availableZones.sort((a, b) => a - b)
      }
      this.droppedItems.splice(index, 1)
    },
    handleDrop(transferData, event) {
      const { type, subtype, databaseField, placeholder, styles, toolbar, meta, fields } =
        transferData
      const { height: h, width: w } = styles
      const index = this.droppedItems.length

      this.droppedItems.push({
        id: uuidv4(),
        type,
        subtype,
        placeholder,
        databaseField,
        meta,
        styles,
        toolbar,
        active: true,
        resizable: true,
        fields,
        z: type === 'shape' ? 1 : 2,
        x: event.offsetX,
        y: event.offsetY,
        w,
        h,
      })
      this.onFocusItem({ styles, toolbar }, index)

      if (type === 'zone') {
        const zoneIndex = this.availableZones.indexOf(placeholder)
        this.availableZones.splice(zoneIndex, 1)
      }
    },
    uploadZoneImage(index) {
      this.activeZoneIndex = index
      this.$refs['upload-zone-image'].$refs['input'].click()
    },
    async uploadBackground(dataURL) {
      const mime = getMimeFromDataURL(dataURL)
      const image = await dataURLToImage(dataURL)
      const file = await resizeImage(image, {
        to: 'file',
        mime,
        width: this.toPixels(this.getWidth()),
        height: this.toPixels(this.getHeight()),
      })
      const path = `${this.eventId}/avatars/${uuidv4()}`
      const url = await this.uploadImage(file, path)
      this.backgroundImageURL = url
    },
    async uploadImage(file, path) {
      const { upload: uploadURL, download: downloadURL } = await getSignedURL(path)
      await uploadImage(uploadURL, file)

      return downloadURL
    },
    async processImage(f) {
      const image = await fileToImage(f)
      const file = await resizeImage(image, { to: 'file', mime: f.type })
      const path = `${this.eventId}/builder/${uuidv4()}`
      const httpURL = await this.uploadImage(file, path)
      const activeZone = this.droppedItems[this.activeZoneIndex]
      const { styles, ...rest } = activeZone
      this.updateItem(this.activeZoneIndex, {
        ...rest,
        styles: { ...styles, backgroundImage: httpURL },
      })
    },
    async saveBuilder() {
      await this.$apollo.mutate({
        mutation: SAVE_BUILDER,
        variables: {
          eventID: this.eventId,
          items: this.droppedItems,
          backgroundImage: this.backgroundImageURL,
          format: this.options.format.name,
          orientation: this.options.orientation,
        },
      })
      this.snackbarOpen = true
    },
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_BUILDER,
      variables() {
        return {
          id: this.eventId,
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const { getEvent: event } = data
          this.extraModules = event.extraModules
          this.fields = objectToArray(event.attendeeFields, 'name')
          const allZones = [...Array(event.numberOfZones).keys()].map(z => z + 1)
          if (!event.builder) {
            this.availableZones = allZones
            this.droppedItems = []
            const defaultFormat = formats[0]
            this.options = {
              orientation: defaultFormat.defaultOrientation,
              format: defaultFormat,
            }
          } else {
            const items = event.builder.items || []
            const zoneItems = items
              .filter(({ type }) => type === 'zone')
              .map(({ placeholder }) => placeholder)
            this.availableZones = allZones.filter(x => !zoneItems.includes(x))
            const { format, orientation } = event.builder
            const defaultFormat = formats.find(f => f.name === format)
            this.options = {
              orientation,
              format: defaultFormat,
            }

            this.previewData = {
              ...this.previewData,
              fields: getSampleFieldsValue(event.attendeeFields),
            }
            this.backgroundImageURL = event.builder.backgroundImage
            this.droppedItems = items
          }
        }
      },
    },
  },
}
</script>

<style scoped>
.active-item {
  border: 2px #0277bd dashed !important;
}

.item-action {
  background: #0277bd;
  border: 0;
  padding: 1px;
  border-radius: 50%;
  box-sizing: border-box;
  display: block;
  position: absolute;
  font-size: 1px;
  cursor: pointer;
}

.upload-zone-image {
  bottom: -10px;
  right: -10px;
}
.delete {
  top: -10px;
  right: -10px;
}

.rotate {
  right: -10px;
  bottom: -10px;
}

.drop-zone {
  position: relative;
  margin: 15px auto;
  background-color: white;
}

.button-container {
  margin: 15px auto !important;
}

.parent-container {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px #979797 dashed;
  border-radius: 8px;
}

.builder-toolbar {
  border: 1px solid #979797;
  min-height: 38px;
  background-color: #fff;
}

.builder-toolbar span {
  display: block;
  margin: 7.5px 15px;
}

.upload-container {
  position: relative;
  background-color: #fafafa;
  padding-left: 0;
}

.no-select {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  cursor: grab;
}
.left-container {
  padding: 0 12px;
}
.right-container {
  padding: 0 12px;
}
.not-available {
  display: none;
}
.not-available h1 {
  color: #979797;
  line-height: 24px;
  font-weight: 600;
  font-family: 'Raleway';
  font-size: 20px;
  text-align: center;
}
@media (max-width: 800px) {
  .design-tool-container {
    display: none;
  }
  .not-available {
    display: flex;
    height: calc(100vh - 136px);
    align-items: center;
  }
}
</style>
